import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { CandidateStatus, RecordingStatus } from '../../../../../firebase/firestore/documents/candidate';
import CandidatePickerItemPresenter from './item/candidatePickerItemPresenter';
import { ModernHireStatusList } from '../../../../../firebase/firestore/documents/modernHireTypes';

// Helper function to get the LE status.
const getLEStatus = (candidate: CandidatePickerItemPresenter, t: TFunction): string => {
  const { recordingStatus, languageEvaluationReview } = candidate.candidate;
  /* eslint-disable-next-line */
  const LETitle = t('common:tabs.audioRecording') + ' - ';
  // If LE is COMPLETED but rating is not available
  if (recordingStatus === RecordingStatus.REVIEW_COMPLETED && !languageEvaluationReview?.rating) {
    return LETitle + t(`audioEvaluation:status.TO_BE_ACCESSED`);
  }
  if (
    recordingStatus === RecordingStatus.REQUESTED ||
    recordingStatus === RecordingStatus.IN_REVIEW ||
    recordingStatus === RecordingStatus.RE_REQUESTED
  ) {
    return LETitle + t(`audioEvaluation:status.${recordingStatus}`);
  }

  return LETitle + t(`audioEvaluation:status.${languageEvaluationReview?.rating}`);
};

// Helper function to get the modern hire status
const getModernHireStatus = (candidate: CandidatePickerItemPresenter, t: TFunction): string => {
  // If modern hire is passed show as COMPETENCY_EVALUATION_COMPLETED
  if (candidate.candidate.modernHire?.status === ModernHireStatusList.ASSESSMENT_PASSED) {
    return t(`modernHire:status.COMPETENCY_EVALUATION_COMPLETED`);
  }
  return t(`modernHire:status.${candidate.candidate.modernHire?.status}`);
};

// Helper function to get the offer rejected/revoked status
const getOfferRejectedStatus = (candidate: CandidatePickerItemPresenter, t: TFunction): string => {
  const currentDetailStatus = candidate.candidate.detailStatus?.current;
  if (currentDetailStatus === 'OFFER_REVOKE' || currentDetailStatus === 'OFFER_REVOKED') {
    return t('candidateStatus:OFFER_REVOKED');
  }
  return t('candidateStatus:OFFER_REJECTED');
};

// An array of rules to determine the candidate's status.
// Each rule has a condition and a function to return the status string.
// order of rules in this array is important as the first rule that matches the candidate will be returned as the status
const statusRules = [
  {
    // Deactivated status
    condition: (candidate: CandidatePickerItemPresenter) => candidate.candidate.deactivated,
    result: (t: TFunction) => t('candidateStatus:DEACTIVATED'),
  },
  {
    // Hire form in progress
    condition: (candidate: CandidatePickerItemPresenter) => candidate.candidate.hireDetailsStatus === 'inProgress',
    result: (t: TFunction) => t('candidateStatus:HIRE_FORM_IN_PROGRESS'),
  },
  {
    // Hire form requested or needs action
    condition: (candidate: CandidatePickerItemPresenter) =>
      candidate.candidate.hireDetailsStatus === 'requested' || candidate.candidate.hireDetailsStatus === 'needsAction',
    result: (t: TFunction) => t('candidateStatus:HIRE_FORM_REQUESTED'),
  },
  {
    // Hire form help requested
    condition: (candidate: CandidatePickerItemPresenter) => candidate.candidate.hireDetailsStatus === 'helpRequested',
    result: (t: TFunction) => t('candidateStatus:HIRE_FORM_HELP_REQUESTED'),
  },
  {
    // Offer accepted
    condition: (candidate: CandidatePickerItemPresenter) =>
      candidate.status === CandidateStatus.OfferMade && candidate.candidate.offerAcceptanceStatus?.offerAccepted,
    result: (t: TFunction) => t('candidateStatus:OFFER_ACCEPTED'),
  },
  {
    // Offer revoked / Offer rejected
    condition: (candidate: CandidatePickerItemPresenter) => candidate.status === CandidateStatus.OfferRejected,
    result: (t: TFunction, candidate: CandidatePickerItemPresenter) => getOfferRejectedStatus(candidate, t),
  },
  {
    // LE status
    condition: (candidate: CandidatePickerItemPresenter) =>
      (candidate.status === CandidateStatus.Liked || candidate.status === CandidateStatus.ContactRequestAccepted) &&
      !!candidate.candidate.recordingStatus,
    result: (t: TFunction, candidate: CandidatePickerItemPresenter) => getLEStatus(candidate, t),
  },
  {
    // Modern Hire status
    condition: (candidate: CandidatePickerItemPresenter) =>
      (candidate.status === CandidateStatus.Liked || candidate.status === CandidateStatus.ContactRequestAccepted) &&
      candidate.candidate.modernHireEnable &&
      !!candidate.candidate.modernHire,
    result: (t: TFunction, candidate: CandidatePickerItemPresenter) => getModernHireStatus(candidate, t),
  },
];

export default function useGetCandidatePositionStatus(candidate: CandidatePickerItemPresenter | null): () => string {
  const { t } = useTranslation();

  return React.useCallback(() => {
    if (!candidate || !candidate.candidate) {
      return '';
    }

    // Find the first rule that matches the candidate's data.
    /* eslint-disable-next-line */
    for (const rule of statusRules) {
      if (rule.condition(candidate)) {
        return rule.result(t, candidate);
      }
    }

    // If no specific rule matches, return the default candidate status.
    return t(`candidateStatus:${candidate.status}`);
  }, [candidate, t]);
}
